import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { CHIVO, HOME_DESCRIPTION, HOME_TITLE, Page, money, useMobile, useSite } from "@/components/site/Chrome";
import { HeroScoreCard } from "@/components/site/ScoreCard";

const h2 = (mob) => ({ marginTop: 14, fontFamily: CHIVO, fontWeight: 900, letterSpacing: "-0.035em", lineHeight: 1.05, fontSize: mob ? 30 : 38 });

const cap = (w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : "");

function ExamFactsLine({ c, light }) {
  const f = c.exam_facts;
  if (!f) return null;
  const color = light ? "#B9C4CE" : "var(--ink-3)";
  return (
    <p data-testid={`catalog-facts-${c.cert_slug}`} style={{ marginTop: 10, fontSize: 14, color, lineHeight: 1.5 }}>
      Real exam: <span className="dash">{f.question_count}</span> questions · <span className="dash">{f.time_limit_min}</span> minutes · pass at <span className="dash">{f.passing_score}</span>
    </p>
  );
}

function Hero({ mob, c }) {
  const site = useSite();
  return (
    <section data-testid="home-hero" style={{ position: "relative", background: "#FFFFFF", ...(mob
      ? { padding: "44px 20px 76px", display: "flex", flexDirection: "column", gap: 40 }
      : { padding: "92px 32px 140px", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(400px,1fr))", gap: 56, alignItems: "center" }) }}>
      <div style={{ position: "relative", zIndex: 4 }}>
        <p className="kicker" style={{ letterSpacing: "0.14em" }}>CERTIFICATION PRACTICE EXAMS</p>
        <h1 data-testid="home-h1" style={{ marginTop: 22, fontFamily: CHIVO, fontWeight: 900, lineHeight: 1.03, letterSpacing: "-0.035em", textWrap: "pretty", maxWidth: "15em", fontSize: mob ? 40 : 68 }}>Real practice exams that get you certified.</h1>
        <p data-testid="hero-subhead" style={{ marginTop: 24, fontSize: 19, lineHeight: 1.5, color: "var(--ink-3)", maxWidth: "22em" }}>{c ? `${cap(c.exams_word)} full-length` : "Full-length"} exams per certification, timed and weighted like the real thing.</p>
        <div style={{ marginTop: 36, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 22 }}>
          <a href="#catalog" className="btn primary hero" data-testid="hero-find-exam">Find your exam</a>
          <p style={{ fontSize: 15, color: "var(--ink-3)" }}>Pass or your money back. {site?.access_months} months of access.</p>
        </div>
        {!mob && (
          <div className="hero-ray" data-testid="hero-ray" style={{ position: "absolute", right: -20, bottom: -140, zIndex: 2, pointerEvents: "none" }}>
            <img src="/ray.png" alt="" style={{ width: 160, maxWidth: "none", display: "block" }} />
          </div>
        )}
      </div>
      <HeroScoreCard mob={mob} />
    </section>
  );
}

function CertPanel({ c, mob, first }) {
  const site = useSite();
  return (
    <section id={first ? "catalog" : undefined} data-testid="catalog-panel" style={{ background: "#FFFFFF", padding: mob ? "56px 20px" : "136px 32px 72px" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <h2 style={h2(mob)} data-testid="catalog-title"><span className="dash">{c.cert_short}</span> <span className="dash">{c.exam_code}</span></h2>
        <p className="kicker"><span className="dash">{c.vendor.toUpperCase()}</span> · MORE CERTIFICATIONS IN PRODUCTION</p>
      </div>
      <div style={{ marginTop: 26, border: "2px solid var(--ink)", display: "grid", gridTemplateColumns: mob ? "minmax(0,1fr)" : "minmax(0,1.3fr) minmax(0,0.7fr)" }}>
        <div style={{ padding: "34px 34px 38px" }}>
          <p className="kicker" style={{ marginBottom: 6 }}>EXAM DOMAINS &amp; WEIGHTING</p>
          {c.domains.map((d) => (
            <div key={d.id} style={{ display: "grid", gridTemplateColumns: "44px minmax(0,1fr) 58px", gap: 14, alignItems: "baseline", padding: "14px 0", borderTop: "1px solid var(--hair)" }}>
              <span className="mono" style={{ fontSize: 12, color: "#6B7076" }}>{d.id}</span>
              <span style={{ fontSize: 16 }}>{d.name}</span>
              <span className="mono" style={{ fontSize: 14, textAlign: "right" }}>{d.weight}%</span>
            </div>
          ))}
        </div>
        <div style={{ background: "var(--ink)", display: "flex", flexDirection: "column", justifyContent: "center", padding: mob ? "30px 26px 34px" : 34 }}>
          <p className="kicker" style={{ color: "#8A93A0" }}>WHAT YOU GET</p>
          <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 12, fontSize: 16, color: "#FFFFFF" }}>
            <span><span className="dash">{c.exams_per_cert}</span> full-length exams, <span className="dash">{c.bank_total}</span> questions</span>
            <span>Every answer explained</span>
            <span><span className="dash">{site?.access_months}</span> months of access</span>
          </div>
          <ExamFactsLine c={c} light />
          <p style={{ marginTop: 28, fontFamily: CHIVO, fontWeight: 900, fontSize: 52, letterSpacing: "-0.04em", color: "#FFFFFF" }} data-testid="catalog-price">{money(c.price)}</p>
          <Link to={`/${c.cert_slug}`} className="btn primary lg" data-testid={`catalog-start-${c.cert_slug}`} style={{ marginTop: 20, alignSelf: "flex-start" }}>{`Start ${c.cert_short}`}</Link>
        </div>
      </div>
    </section>
  );
}

function CertGrid({ certs, mob }) {
  return (
    <section id="catalog" data-testid="catalog-grid" style={{ background: "#FFFFFF", padding: mob ? "56px 20px" : "136px 32px 72px" }}>
      <p className="kicker">CATALOG · {certs.length} PUBLISHED CERTIFICATIONS</p>
      <h2 style={h2(mob)} data-testid="catalog-title">Pick your certification.</h2>
      <p style={{ marginTop: 14, fontSize: 17, lineHeight: 1.55, color: "var(--ink-3)", maxWidth: "34em" }}>Every certification is a complete collection: full-length exams, every answer explained, one price.</p>
      <div style={{ marginTop: 30, display: "grid", gap: 18, gridTemplateColumns: mob ? "minmax(0,1fr)" : "repeat(auto-fit,minmax(230px,1fr))" }}>
        {certs.map((c) => (
          <div key={c.cert_slug} data-testid={`catalog-card-${c.cert_slug}`} style={{ border: "2px solid var(--ink)", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "26px 24px", flex: 1 }}>
              <p className="kicker"><span className="dash">{c.vendor.toUpperCase()}</span> · <span className="dash">{c.exam_code}</span></p>
              <p style={{ marginTop: 12, fontFamily: CHIVO, fontWeight: 900, fontSize: 26, lineHeight: 1.1, letterSpacing: "-0.03em" }}><span className="dash">{c.cert_short}</span></p>
              <p style={{ marginTop: 16, fontSize: 15, color: "var(--ink-3)" }}><span className="dash">{c.exams_per_cert}</span> exams · <span className="dash">{c.bank_total}</span> questions</p>
              <ExamFactsLine c={c} />
            </div>
            <div style={{ background: "var(--ink)", padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
              <span style={{ fontFamily: CHIVO, fontWeight: 900, fontSize: 26, color: "#FFFFFF", letterSpacing: "-0.03em" }}>{money(c.price)}</span>
              <Link to={`/${c.cert_slug}`} className="btn primary" style={{ padding: "13px 22px", fontSize: 15 }} data-testid={`catalog-view-${c.cert_slug}`}>View exams</Link>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function StatStrip({ certs, mob, grid }) {
  const c = certs[0];
  const big = { fontFamily: CHIVO, fontWeight: 900, fontSize: 64, lineHeight: 1, letterSpacing: "-0.045em" };
  const sub = { marginTop: 12, fontSize: 16, color: "var(--ink-3)" };
  return (
    <section data-testid="stat-strip" style={{ background: "#FFFFFF", borderTop: "1px solid var(--hair)", display: "grid", gap: mob ? 28 : 44, gridTemplateColumns: mob ? "minmax(0,1fr)" : "repeat(3,minmax(0,1fr))", padding: mob ? "48px 20px" : "64px 32px 72px" }}>
      <div style={{ padding: "8px 0" }}><p style={big}><span className="dash">{c.exams_per_cert}</span></p><p style={sub}>Full-length exams per certification, timed and weighted like the real thing.</p></div>
      <div style={{ padding: "8px 0" }}><p style={big}><span className="dash">{c.bank_total}</span></p><p style={sub}>{grid ? "Questions per set" : <>Questions in the <span className="dash">{c.cert_short}</span> set</>}, with no item repeated between exams.</p></div>
      <div style={{ padding: "8px 0" }}><p style={big}>100%</p><p style={sub}>Every answer explained — why the right one is right, why each wrong one is wrong.</p></div>
    </section>
  );
}

export function DarkBand({ mob, kicker, title, body, cta, to, children }) {
  return (
    <section data-testid="dark-band" style={{ position: "relative", overflow: "hidden", background: "var(--deep)", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 32, padding: mob ? "56px 20px" : "88px 32px" }}>
      <div style={{ position: "relative", zIndex: 2, flex: "1 1 420px" }}>
        <p className="mono" style={{ fontSize: kicker.length > 14 ? 11 : 10, letterSpacing: "0.14em", color: "var(--ac-light)" }}>{kicker}</p>
        <h1 style={{ marginTop: 18, color: "#FFFFFF", fontFamily: CHIVO, fontWeight: 900, letterSpacing: "-0.035em", lineHeight: 1.02, fontSize: mob ? 36 : 54 }}>{title}</h1>
        <p style={{ marginTop: 18, fontSize: 17, lineHeight: 1.55, color: "#B9C4CE", maxWidth: "30em" }}>{body}</p>
        {children}
      </div>
      {cta && <Link to={to} className="btn lg" data-testid="dark-band-cta" style={{ position: "relative", zIndex: 2, background: "var(--ac)", color: "#04100E", border: 0, padding: "19px 38px" }}>{cta}</Link>}
    </section>
  );
}

export function FreeCapture({ mob, certs }) {
  const navigate = useNavigate();
  const [slug, setSlug] = useState(certs.length === 1 ? certs[0].cert_slug : "");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const c = certs.find((x) => x.cert_slug === slug) || certs[0];
  if (!c) return null;
  const send = async (e) => {
    e.preventDefault();
    if (!slug) { toast.error("Pick a certification first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/free/start", { cert_slug: slug, email: email.trim().toLowerCase() });
      navigate(`/free?sent=1&email=${encodeURIComponent(email.trim().toLowerCase())}&cert=${slug}${data.dev_magic_link ? `&dev=${encodeURIComponent(data.dev_magic_link)}` : ""}`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const box = { height: 60, border: "1px solid var(--ink)", borderRadius: 4, fontSize: 16, background: "#FFFFFF", color: "var(--ink)" };
  return (
    <section data-testid="free-capture" style={{ background: "#FFFFFF", padding: mob ? "8px 20px 64px" : "16px 32px 88px" }}>
      <div style={{ border: "2px solid var(--ink)", padding: "38px 34px" }}>
        <p className="kicker">FREE SAMPLE · <span className="dash">{c.free_count}</span> QUESTIONS · <span className="dash">{c.free_minutes}</span> MINUTES</p>
        <h2 style={h2(mob)}>Start with a free sample exam.</h2>
        <p style={{ marginTop: 14, fontSize: 17, lineHeight: 1.55, color: "var(--ink-3)", maxWidth: "34em" }}>A real <span className="dash">{c.free_count}</span>-question exam from your certification, timed and scored with the same domain report as the paid set.</p>
        <form onSubmit={send} style={{ marginTop: 26, display: "flex", flexWrap: "wrap", alignItems: "stretch", gap: 10 }}>
          <select data-testid="free-cert-select" value={slug} onChange={(e) => setSlug(e.target.value)} style={{ ...box, flex: "1 1 210px", padding: "0 16px" }}>
            <option value="" disabled>Which exam?</option>
            {certs.map((x) => <option key={x.cert_slug} value={x.cert_slug}>{x.cert_short}</option>)}
          </select>
          <input data-testid="free-email-input" type="email" required placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} style={{ ...box, flex: "1 1 240px", padding: "0 18px" }} />
          <button data-testid="free-submit" disabled={busy} className="btn primary" style={{ flex: "0 0 auto", height: 60, padding: "0 34px", fontSize: 18 }}>{busy ? "Sending…" : "Send my free exam"}</button>
        </form>
        <p style={{ marginTop: 14, fontSize: 14, color: "#6B7076" }}>Magic link, no password, no card.</p>
      </div>
    </section>
  );
}

export default function Landing() {
  const mob = useMobile();
  const site = useSite();
  const certs = site?.certs || [];
  const grid = site?.catalog_layout === "grid";
  return (
    <Page title={HOME_TITLE} description={HOME_DESCRIPTION} testid="landing-page">
      <Hero mob={mob} c={certs[0]} />
      {site && certs.length === 0 && (
        <section id="catalog" data-testid="catalog-empty" style={{ padding: mob ? "56px 20px" : "136px 32px 72px" }}>
          <p className="kicker">CATALOG</p>
          <h2 style={h2(mob)}>Certifications in production.</h2>
          <p style={{ marginTop: 14, fontSize: 17, color: "var(--ink-3)" }}>The first published certification will appear here automatically.</p>
        </section>
      )}
      {certs.length > 0 && (grid ? <CertGrid certs={certs} mob={mob} /> : certs.map((c, i) => <CertPanel key={c.cert_slug} c={c} mob={mob} first={i === 0} />))}
      {certs.length > 0 && <StatStrip certs={certs} mob={mob} grid={grid} />}
      <DarkBand mob={mob} kicker="THE GUARANTEE" title="Pass, or your money back." cta="See the terms" to="/guarantee"
        body={<>Clear {site?.guarantee?.pass_pct ?? 85}% on all {certs[0]?.exams_word || "ten"} exams, sit the real thing inside your window, and if you fail we refund your purchase price.</>} />
      {certs.length > 0 && <FreeCapture mob={mob} certs={certs} />}
    </Page>
  );
}
